import { observer } from "mobx-react";
import FontBody1 from "../../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../../components/fonts/font-props";
import { Input } from "../../../../../../components/inputs/input/input";
import { DROPDOWN_MENU_WIDTH, SelectDropdown } from "../../../../../../components/inputs/select-dropdown/select-dropdown";
import RefundCalculatorStore, { IRefundOption } from "../../../stores/refund-calculator-store";
import FontBody2 from "../../../../../../components/fonts/font-body2/font-body2";
import "./refund-actions.scss";
import Currency from "../../../../../../components/currency/currency";
import { Toggle } from "../../../../../../components/inputs/toggle/toggle";
import { Button } from "../../../../../../components/inputs/button/button";
import { BUTTON_VARIANTS } from "../../../../../../components/inputs/button-variants";
import CurrencyService from "../../../../../../services/currency-service";
import { TYPE, rootContainer } from "../../../../../../services/container/root";
import RmaPageStore from "../../../stores/rma-page-store";
import { NOTE_TYPE, Note } from "../../../../../../components/note/note";
import IconCopy from "../../../../../../components/icons/icon-copy/icon-copy";
import { ButtonIcon } from "../../../../../../components/inputs/button-icon/button-icon";
import useValidationGroup from "../../../../../../hooks/validation-group/validation-group-hook";
import RestockNote from "../../restock-ntoe/restock-note";
import useDashboardTranslation from "../../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { Tooltip } from "../../../../../../components/tooltip/tooltip";
import Icon from "../../../../../../components/icons/icon/icon";
import IconInfo from "../../../../../../components/icons/icon-info/icon-info";
import IconWarning from "../../../../../../components/icons/icon-warning/icon-warning";
import { REFUND_METHODS } from "../refund-calculator-types";
import rmsMixpanelStore from "../../../../../../stores/rms-mixpanel-store";

interface IRefundActionsProps {
    calculatorStore: RefundCalculatorStore;
    rma: RmaPageStore;
    onCancelClick: () => void;
}

const RefundActions: React.FC<IRefundActionsProps> = observer(({calculatorStore, rma, onCancelClick}) => {
    const currencyService = rootContainer.get<CurrencyService>(TYPE.CurrencyService);
    const refundMethodOptions = calculatorStore.getRefundMethodOptions();
    const selectedRefundOption = refundMethodOptions.find(option => option.value === calculatorStore.selectedRefundMethodId);
    const restockLocationsOptions = calculatorStore.getRestockingLocationsList();
    const selectedrestockLocationOption = restockLocationsOptions.find(option => option.value === calculatorStore.restockLocationId);
    const emailToggleText = calculatorStore.getEmailToggleText();
    const handleCopyClick = () => {
        navigator.clipboard.writeText(`${calculatorStore.IBANDetails?.iban_number}`);
    };
    const restrictionsNoteText = calculatorStore.getRestrictionsNoteText();
    const giftCardOrCreditsNoteText = calculatorStore.getGiftCardOrCreditsNoteText();
    const availableAmount = calculatorStore.getAvailableAmount();

    const vGroup = useValidationGroup();

    const noneRestockableItemsCount = rma.getNoneRestockableSelectedItemsCount();
    const { getTranslation} = useDashboardTranslation();
    const pageScope = 'dashboard-returns-rma-card';

    const getRefundButtonText = ():string => {
        if (calculatorStore.selectedRefundMethodType === REFUND_METHODS.BANK_REFUND) {
            return getTranslation('MARK_REFUNDED', pageScope);
        }
        return getTranslation('REFUND', pageScope);
    } 

    const onRefundMethodChange = (o: IRefundOption) => {
        rmsMixpanelStore.trackEvent('d&_calculator_refund_method');
        calculatorStore.setSelectedRefundOption(o);
        vGroup.validate();
    }

    const onRestockChange = (v: boolean) => {
        rmsMixpanelStore.trackEvent('d&_calculator_restock_toggle');
        calculatorStore.setRestock(v);
    }

    const onNotifyOnPlatformChange = (v: boolean) => {
        rmsMixpanelStore.trackEvent('d&_calculator_send_platform_email_notifications');
        calculatorStore.setNotifyOnPlatform(v);
    }

    const onCancelButtonClick = () => {
        rmsMixpanelStore.trackEvent('d&_calculator_cancel_button');
        onCancelClick();
    }

    const onRefundButtonClick = () => {
        rmsMixpanelStore.trackEvent('d&_calculator_refund_button');
        rma.refund(onCancelClick);
    }

    return(
        <div className="refund-actions">
            <div className="action-lines">
                <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('ISSUE_REFUND', pageScope)}</FontBody1>
                { !!restrictionsNoteText &&
                    <Note type={NOTE_TYPE.INFORMATION} body={restrictionsNoteText} />
                }
                <div className="action-line">
                    <FontBody2 color={FONT_COLOR.PRIMARY2}>{getTranslation('REFUND_METHOD', pageScope)}</FontBody2>                
                    <div className="refund-method">
                        <SelectDropdown
                            options={refundMethodOptions}
                            value={selectedRefundOption}
                            onChange={(o) => onRefundMethodChange(o as IRefundOption)}
                        />
                    </div>
                </div>
                <div className="action-line">
                    <FontBody2 color={FONT_COLOR.PRIMARY2}>{getTranslation('REFUND_AMOUNT', pageScope)}</FontBody2>
                    <div className="refund-amount-input">
                        <Input 
                            value={calculatorStore.getActualRefundAmount()} 
                            placeholder="Type amount"
                            type="text"
                            onChange={(v) => {calculatorStore.setActualRefundAmount(v);}}
                            adornment={currencyService.getCurrencySymbol(calculatorStore.paymentCurrency)}
                            validationCallback={calculatorStore.validateRefundAmount}
                            validationGroup={vGroup}
                            required
                        />
                    </div>
                </div>
                { !!calculatorStore.shouldShowAvailableAmount() && 
                    <div className="action-line">
                        <div className="available-amount-title">
                            <FontBody2 color={FONT_COLOR.PRIMARY2}>{getTranslation('AVAILABLE_AMOUNT', pageScope)}</FontBody2>
                            {availableAmount === 0 && (
                                calculatorStore.isOriginalPaymentCaptured ? (
                                        <Tooltip content={<div className="refund-calculator-available-amount-tooltip-content">
                                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                                {getTranslation('ZERO_REFUND_AMOUNT_FULLY_REFUNDED', pageScope)}
                                            </FontBody2>
                                        </div>}>
                                            <Icon Component={IconInfo} />
                                        </Tooltip>) : (<Tooltip content={<div className="refund-calculator-available-amount-tooltip-content">
                                        <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                            {getTranslation('ZERO_REFUND_AMOUNT_PAYMENT_NOT_CAPTURED', pageScope)}
                                        </FontBody2>
                                    </div>}>
                                        <Icon Component={() => <IconWarning color={'var(--color-semantic-warning)'} />} />
                                    </Tooltip>)
                            )}
                        </div>
                        <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                            <Currency amount={availableAmount} currencyCode={calculatorStore.paymentCurrency} />
                        </FontBody2>
                    </div>
                }
                { !!calculatorStore.showIBANSection() && calculatorStore.IBANDetails &&
                    <>
                        <hr/>
                        <div className="action-line">
                            <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('IBAN_NUMBER', pageScope)}</FontBody1>
                            <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{calculatorStore.IBANDetails.iban_number}</FontBody2>
                            <ButtonIcon Component={IconCopy} onClick={handleCopyClick} />
                        </div>
                        <div className="action-line">
                            <FontBody1 color={FONT_COLOR.PRIMARY2}>{getTranslation('FIRST_NAME', pageScope)}</FontBody1>
                            <FontBody2 color={FONT_COLOR.PRIMARY2} >{calculatorStore.IBANDetails.first_name ?? "--"}</FontBody2>
                        </div>
                        <div className="action-line">
                            <FontBody1 color={FONT_COLOR.PRIMARY2}>{getTranslation('LAST_NAME', pageScope)}</FontBody1>
                            <FontBody2 color={FONT_COLOR.PRIMARY2}>{calculatorStore.IBANDetails.last_name ?? "--"}</FontBody2>
                        </div>
                        <div className="action-line">
                            <FontBody1 color={FONT_COLOR.PRIMARY2}>{getTranslation('BRANCH', pageScope)}</FontBody1>
                            <FontBody2 color={FONT_COLOR.PRIMARY2}>{calculatorStore.IBANDetails.branch_name ?? "--"}</FontBody2>
                        </div>
                        <div className="action-line">
                            <FontBody1 color={FONT_COLOR.PRIMARY2}>{getTranslation('BRANCH_ADDRESS', pageScope)}</FontBody1>
                            <FontBody2 color={FONT_COLOR.PRIMARY2}>{calculatorStore.IBANDetails.branch_address ?? "--"}</FontBody2>
                        </div>
                        <div className="action-line">
                            <FontBody1 color={FONT_COLOR.PRIMARY2}>{getTranslation('COMMENT', pageScope)}</FontBody1>
                            <FontBody2 color={FONT_COLOR.PRIMARY2}>{calculatorStore.IBANDetails.comment ?? "--"}</FontBody2>
                        </div>
                    </>
                }
                { !!giftCardOrCreditsNoteText &&
                    <Note type={NOTE_TYPE.INFORMATION} body={getTranslation(giftCardOrCreditsNoteText, pageScope)} />
                }
                <hr/>
                <div className="additional-actions-title">
                    <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('ADDITIONAL_ACTIONS', pageScope)}</FontBody1>   
                </div>
                { !!noneRestockableItemsCount && calculatorStore.restock &&
                    <RestockNote
                        noneRestockableItemsCount={noneRestockableItemsCount}
                        selectedItemsCount={rma.selectedProductCases.length}
                    />
                }
                <div className="action-line">
                    <FontBody2 color={FONT_COLOR.PRIMARY2}>{getTranslation('RESTOCK', pageScope)}</FontBody2>
                    <Toggle
                        label=""
                        checked={calculatorStore.restock}
                        onChange={v => onRestockChange(v)}
                        disabled={rma.isRestockDisabled()}
                    />
                </div>
                {!!calculatorStore.restock &&
                    <div className="action-line">
                    <FontBody2 color={FONT_COLOR.PRIMARY2}>{getTranslation('RESTOCKING_LOCATIONS', pageScope)}</FontBody2>
                        <SelectDropdown
                            options={restockLocationsOptions}
                            value={selectedrestockLocationOption}
                            onChange={v => calculatorStore.setRestockLocationId(v?.value as string)}
                            menuWidth={DROPDOWN_MENU_WIDTH.MAX_CONTENT}
                        />
                    </div>
                }
                { !calculatorStore.showIBANSection() &&
                    <div className="action-line">
                        <FontBody2 color={FONT_COLOR.PRIMARY2}>{getTranslation(emailToggleText, pageScope)}</FontBody2>
                        <Toggle
                            label=""
                            checked={calculatorStore.notifyOnPlatform}
                            onChange={v => onNotifyOnPlatformChange(v)}
                            disabled={calculatorStore.isSendNotificationToggleDisabled()}
                        />
                    </div>
                }

                { !!calculatorStore.showRestockWarningNote() &&
                    <Note type={NOTE_TYPE.WARNING} body={getTranslation('RESTOCK_NOTE', pageScope)} />
                }
            </div>
            <div className="actions-buttons">
                <Button text={getTranslation('CANCEL', pageScope)} onClick={onCancelButtonClick} variant={BUTTON_VARIANTS.Transparent} />
                <Button
                    text={getRefundButtonText()}
                    onClick={onRefundButtonClick}
                    variant={BUTTON_VARIANTS.PrimaryAccent}
                    hasLoader={true}
                    isLoading={rma.isRefunding}
                    disabled={!vGroup.isValid() || !calculatorStore.isRefundValid()}
                />
            </div>
        </div>
    );
});

export default RefundActions;
